(() => {
  const descriptionElementName = 'hydro-oasis-description-disclosure';

  if (!customElements.get(descriptionElementName)) {
    class HydroOasisDescriptionDisclosure extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.content = this.querySelector('[data-ho-description-content]');
        this.toggle = this.querySelector('[data-ho-description-toggle]');
        if (!this.content || !this.toggle) return;

        this.initialized = true;
        this.handleToggle = this.handleToggle.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.toggle.addEventListener('click', this.handleToggle);
        this.enhance();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.handleToggle);
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.handleResize);
        this.preview?.remove();
        if (this.content) this.content.hidden = false;
        if (this.toggle) {
          this.toggle.hidden = true;
          this.toggle.setAttribute('aria-expanded', 'false');
          this.toggle.textContent = this.toggle.dataset.labelExpand;
        }
        this.classList.remove('is-enhanced', 'is-expanded');
        this.preview = null;
        this.initialized = false;
      }

      enhance() {
        const previewText = this.content.textContent.replace(/\s+/g, ' ').trim();
        if (!previewText) return;

        this.preview = this.content.cloneNode(false);
        this.preview.removeAttribute('id');
        this.preview.removeAttribute('data-ho-description-content');
        this.preview.classList.add('ho-description-disclosure__preview');
        this.preview.dataset.hoDescriptionPreview = '';
        this.preview.textContent = previewText;
        this.content.before(this.preview);

        this.lastWidth = this.clientWidth;
        this.updateAvailability();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.resizeObserver.observe(this);
        } else {
          window.addEventListener('resize', this.handleResize, { passive: true });
        }
      }

      handleResize(entries) {
        const width = entries?.[0]?.contentRect?.width ?? this.clientWidth;
        if (Math.abs(width - this.lastWidth) < 1) return;

        this.lastWidth = width;
        window.requestAnimationFrame(() => this.updateAvailability());
      }

      updateAvailability() {
        if (!this.preview || this.classList.contains('is-expanded')) return;

        this.preview.hidden = false;
        this.content.hidden = true;

        const overflows = this.preview.scrollHeight > this.preview.clientHeight + 1;
        this.toggle.hidden = !overflows;
        this.classList.toggle('is-enhanced', overflows);

        if (!overflows) {
          this.preview.hidden = true;
          this.content.hidden = false;
        }
      }

      handleToggle() {
        const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !expanded;

        this.classList.toggle('is-expanded', nextExpanded);
        this.preview.hidden = nextExpanded;
        this.content.hidden = !nextExpanded;
        this.toggle.setAttribute('aria-expanded', String(nextExpanded));
        this.toggle.textContent = nextExpanded
          ? this.toggle.dataset.labelCollapse
          : this.toggle.dataset.labelExpand;

        if (!nextExpanded) this.updateAvailability();
      }
    }

    customElements.define(descriptionElementName, HydroOasisDescriptionDisclosure);
  }

  if (document.documentElement.dataset.hoShippingTriggerReady === 'true') return;

  document.documentElement.dataset.hoShippingTriggerReady = 'true';
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-ho-shipping-trigger]');
    if (!trigger) return;

    const targetId = trigger.getAttribute('aria-controls');
    const accordion = targetId ? document.getElementById(targetId) : null;
    if (!(accordion instanceof HTMLDetailsElement)) return;

    event.preventDefault();
    accordion.open = true;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    accordion.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });

    window.requestAnimationFrame(() => {
      const postcode = accordion.querySelector('[data-shipping-estimator-postcode]:not([disabled])');
      const focusTarget = postcode || accordion.querySelector('summary');
      focusTarget?.focus({ preventScroll: true });
    });
  });
})();
